import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Zap } from "lucide-react"
import { cn } from "@/lib/utils"
import { countLabel } from "../benchmarkApi/reportData"
import {
  fieldErrorTextClass,
  fieldInputClass,
  fieldInputErrorClass,
  fieldLabelClass,
  primaryButtonClass,
} from "../lightTheme"
import { finalConverterContent } from "./finalConverterContent"

const bodyClass = "max-w-[68ch] text-[18px] font-normal leading-7 text-[#4D4D4D]"

const submitClass = cn(
  primaryButtonClass,
  "h-auto min-h-10 max-w-full gap-1.5 whitespace-normal px-4 py-2 text-left"
)

const emptyFields = { name: "", website: "", phone: "", email: "" }

function websiteError(value) {
  const trimmed = value.trim()
  if (!trimmed) return "Required"
  if (/\s/.test(value)) return "Enter a domain without spaces"
  const host = trimmed.replace(/^https?:\/\//i, "").split("/")[0]
  if (!/^(?:[a-z0-9-]+\.)+[a-z]{2,}$/i.test(host)) return "Enter a website domain"
  return ""
}

function emailError(value) {
  const trimmed = value.trim()
  if (!trimmed) return "Required"
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return "Enter an email address"
  return ""
}

function fieldErrors(fields) {
  return {
    name: fields.name.trim() ? "" : "Required",
    website: websiteError(fields.website),
    phone: fields.phone.trim() ? "" : "Required",
    email: emailError(fields.email),
  }
}

export default function FinalConverterSection({ statsStatus, totalCompanies }) {
  const count = countLabel(statsStatus, totalCompanies)
  const [fields, setFields] = useState(emptyFields)
  const [errors, setErrors] = useState(emptyFields)
  const [accepted, setAccepted] = useState(false)

  function onSubmit(event) {
    event.preventDefault()
    const nextErrors = fieldErrors(fields)
    setErrors(nextErrors)
    if (Object.values(nextErrors).some(Boolean)) return
    setAccepted(true)
  }

  return (
    <section
      id="benchmark-conclusion"
      className="flex w-full scroll-mt-20 translate-x-[calc(50vw-50%-1.5rem)] flex-col items-start gap-8"
    >
      <div className="flex flex-col gap-3">
        <p className="text-[14px] font-medium leading-5 text-[#4D4D4D]">
          {finalConverterContent.eyebrow}
        </p>
        <h2 className="text-balance text-[48px] font-bold leading-[56px] tracking-[-0.04em] text-[#171717] max-md:text-[32px] max-md:leading-10">
          {finalConverterContent.title}
        </h2>
      </div>
      <p className={bodyClass}>{finalConverterContent.body}</p>
      <div className="flex max-w-[68ch] flex-col gap-3">
        <p className="m-0 text-[18px] font-normal leading-7 text-[#4D4D4D]">
          {finalConverterContent.breakdownLead}
          <strong className="font-bold text-[#171717]">
            {finalConverterContent.breakdownName}
          </strong>
          {finalConverterContent.breakdownTail}
        </p>
        <ul className="m-0 flex list-disc flex-col gap-2 pl-5 text-[18px] font-normal leading-7 text-[#4D4D4D]">
          {finalConverterContent.items.map((item) => (
            <li key={item}>{item.replaceAll("{count}", count)}</li>
          ))}
        </ul>
      </div>
      <hr className="m-0 w-full max-w-[68ch] border-0 border-t border-solid border-[#E6E6E6]" />
      <div className="flex w-full max-w-[32rem] flex-col gap-6">
        <h3 className="m-0 text-balance text-[24px] font-bold leading-8 tracking-[-0.04em] text-[#171717]">
          {finalConverterContent.formTitle}
        </h3>
        {accepted ? (
          <p className={bodyClass}>{finalConverterContent.confirmation}</p>
        ) : (
          <form className="flex w-full flex-col gap-4" onSubmit={onSubmit} noValidate>
            {finalConverterContent.fields.map((field) => {
              const error = errors[field.id]
              const errorId = `${field.id}-error`
              return (
                <div key={field.id} className="flex w-full flex-col gap-2">
                  <Label className={fieldLabelClass} htmlFor={`audit-${field.id}`}>
                    {field.label}
                  </Label>
                  <Input
                    id={`audit-${field.id}`}
                    type="text"
                    inputMode={field.inputMode}
                    autoComplete={field.autoComplete}
                    placeholder={field.placeholder}
                    value={fields[field.id]}
                    aria-invalid={error ? "true" : "false"}
                    aria-describedby={error ? errorId : undefined}
                    onChange={(event) =>
                      setFields((current) => ({ ...current, [field.id]: event.target.value }))
                    }
                    className={error ? `${fieldInputClass} ${fieldInputErrorClass}` : fieldInputClass}
                  />
                  {error ? (
                    <p id={errorId} className={fieldErrorTextClass}>
                      {error}
                    </p>
                  ) : null}
                </div>
              )
            })}
            <button type="submit" className={submitClass}>
              <Zap className="size-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
              {finalConverterContent.submitLabel}
            </button>
          </form>
        )}
        <p className="text-[14px] font-normal italic leading-5 text-[#4D4D4D]">
          {finalConverterContent.note}
        </p>
      </div>
    </section>
  )
}
